import { Link } from '@tanstack/react-router';
import { ListChecks } from 'lucide-react';

import { cn } from '@/lib/utils';

import { LayerChip } from './GroundTruthLayerSteps';
import { fieldsLineState } from './layerSteps';

const TONE_CLASS = {
    neutral: 'text-[#476969]',
    warning: 'font-medium text-amber-700',
    success: 'font-medium text-green-700',
} as const;

/** The "3 Fields" line under a document row: how far its fields are filled, with a link to the document page. */
export function DocFieldsLine({
    ingestedFileId,
    docId,
    stale,
    filled,
    total,
    testId,
    quiet = false,
}: {
    ingestedFileId: number;
    /** The saved document this row makes, if any. */
    docId: number | null;
    /** The saved document was made from other pages. */
    stale: boolean;
    filled: number;
    total: number;
    testId: string;
    /** Shows the line only for a stale document with filled answers or one with fields, in a plain grey style. */
    quiet?: boolean;
}) {
    const state = fieldsLineState({
        hasDocument: docId !== null,
        stale,
        filled,
        total,
    });

    if (quiet && (docId === null || (stale ? filled <= 0 : total === 0))) {
        return null;
    }
    const text =
        quiet && stale
            ? `Pages changed: ${filled} filled ${filled === 1 ? 'answer' : 'answers'} kept only if pages match`
            : state.text;

    return (
        <div
            className={cn(
                'mx-3 mb-2 flex flex-wrap items-center gap-2 text-xs',
                quiet && 'ml-10 flex-nowrap items-start gap-1.5',
            )}
            data-testid={testId}
        >
            {quiet ? (
                <ListChecks
                    aria-hidden="true"
                    className={cn(
                        'mt-px h-3.5 w-3.5 shrink-0',
                        state.tone === 'warning'
                            ? 'text-amber-700'
                            : 'text-[#648B8B]',
                    )}
                />
            ) : (
                <LayerChip step="fields" className="h-4 w-4" />
            )}
            <span className={cn(quiet && 'min-w-0', TONE_CLASS[state.tone])}>{text}</span>
            {state.canOpen && (
                <span
                    aria-hidden="true"
                    className="inline-flex h-2 w-16 overflow-hidden rounded-full bg-violet-100"
                >
                    <span
                        className="h-full bg-violet-600"
                        style={{ width: `${state.percent}%` }}
                    />
                </span>
            )}
            {state.canOpen && docId !== null && (
                <Link
                    to="/ground-truth/$ingestedFileId/$docId"
                    params={{ ingestedFileId, docId }}
                    className="ml-auto font-medium text-violet-700 hover:underline"
                >
                    {state.linkLabel} →
                </Link>
            )}
        </div>
    );
}
